import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, BookOpen, ChevronRight, X, RefreshCw, Trash2 } from 'lucide-react';
import { useSettings } from '../../context/SettingsContext';
import { useProviders } from '../../hooks/useProviders';
import ProviderSelector from '../settings/ProviderSelector';
import ModelSelector from '../settings/ModelSelector';
import OllamaConfig from '../settings/OllamaConfig';
import ApiKeyManager from '../settings/ApiKeyManager';
import ApiKeyStatus from '../settings/ApiKeyStatus';
import { isElectron } from '../../utils/electron';
import './Admin.css';

export default function AISettings() {
  const { t } = useTranslation();
  const settings = useSettings();
  const { providers, loading, error, refresh } = useProviders();
  const [saveMessage, setSaveMessage] = useState(null);
  const [helpDismissed, setHelpDismissed] = useState(() => {
    try { return localStorage.getItem('etch.aiHelpDismissed') === '1'; } catch { return false; }
  });

  const selectedProvider = providers.find(p => p.id === settings.provider);

  const showSaved = () => {
    setSaveMessage(t('settings.saved'));
    setTimeout(() => setSaveMessage(null), 2000);
  };

  const handleProviderChange = (providerId) => {
    const provider = providers.find(p => p.id === providerId);
    if (provider) {
      settings.setProvider(providerId, provider.defaultModel);
      showSaved();
    }
  };

  const handleModelChange = (model) => {
    settings.setModel(model);
    showSaved();
  };

  const handleOllamaUrlChange = (url) => {
    settings.setOllamaBaseUrl(url);
    showSaved();
  };

  const handleDismissHelp = () => {
    setHelpDismissed(true);
    try { localStorage.setItem('etch.aiHelpDismissed', '1'); } catch {}
  };

  if (loading) {
    return (
      <div role="status" aria-live="polite" className="admin-loading">
        {t('settings.loadingProviders')}
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert" className="admin-error">
        <p>{t('settings.loadFailed', { error })}</p>
        <button type="button" onClick={refresh} className="btn btn-primary">
          {t('settings.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="admin-stack">
      <header className="admin-section-header admin-section-header--with-status">
        <div>
          <p className="eyebrow">Einstellungen · KI &amp; Modelle</p>
          <h1 className="admin-section-title" id="ai-heading" style={{ fontSize: '1.625rem' }}>
            KI-Anbieter
          </h1>
          <p className="admin-section-desc">
            Wähle den Anbieter für Chat, Karten-Generierung und Erklärungen. API-Keys werden
            verschlüsselt lokal gespeichert (Electron) oder serverseitig in <code>.local.env</code>.
          </p>
        </div>
        {saveMessage && (
          <span className="admin-save-message" role="status" aria-live="polite">
            {saveMessage}
          </span>
        )}
      </header>

      {!helpDismissed && (
        <div className="help-banner" role="note">
          <div className="help-banner-icon" aria-hidden="true">
            <Info size={18} />
          </div>
          <div className="help-banner-body">
            <strong>Wie funktioniert das?</strong>
            <p>
              Du brauchst einen API-Key vom jeweiligen Anbieter. <b>Anthropic</b> und{' '}
              <b>OpenAI</b> kosten pro 1M Token; <b>Ollama</b> läuft lokal und ist kostenlos.
              Pro Anbieter wählst du dann das passende Modell — Sonnet für Lernchats, Haiku
              für günstige Massenoperationen.
            </p>
            <a
              className="help-banner-link"
              href="https://docs.anthropic.com/en/docs/about-claude/models"
              target="_blank"
              rel="noopener noreferrer"
            >
              <BookOpen size={14} /> Detaillierte Anleitung
              <ChevronRight size={14} />
            </a>
          </div>
          <button
            type="button"
            className="help-banner-close"
            aria-label="Hinweis schließen"
            onClick={handleDismissHelp}
          >
            <X size={16} />
          </button>
        </div>
      )}

      <section className="settings-section-card" aria-labelledby="ai-providers-heading">
        <header className="settings-section-head">
          <h2 className="settings-section-title" id="ai-providers-heading">
            Anbieter wählen
          </h2>
          <p className="settings-section-desc">
            Pro Workspace ist ein Anbieter aktiv — du kannst jederzeit wechseln. Die Status-Pille
            zeigt, ob der API-Key bereits eingerichtet ist.
          </p>
        </header>
        <ProviderSelector
          providers={providers}
          selectedProvider={settings.provider}
          onSelect={handleProviderChange}
        />
      </section>

      <section className="settings-section-card" aria-labelledby="ai-keys-heading">
        <header className="settings-section-head">
          <h2 className="settings-section-title" id="ai-keys-heading">
            API-Keys verwalten
          </h2>
          <p className="settings-section-desc">
            {isElectron
              ? 'Keys werden verschlüsselt im macOS-Keychain abgelegt und nie an Dritte weitergegeben. Du kannst sie jederzeit ersetzen oder entfernen.'
              : 'Keys werden serverseitig in der laufenden Session gehalten und beim Neustart geleert. Verwende Electron für persistente, verschlüsselte Speicherung.'}
          </p>
        </header>
        {isElectron
          ? <ApiKeyManager providers={providers} onKeySaved={refresh} />
          : <ApiKeyStatus providers={providers} onKeySaved={refresh} />}
      </section>

      {selectedProvider && (
        <section className="settings-section-card" aria-labelledby="ai-model-heading">
          <header className="settings-section-head">
            <h2 className="settings-section-title" id="ai-model-heading">
              Modell für {selectedProvider.name}
            </h2>
            <p className="settings-section-desc">
              Innerhalb des aktiven Anbieters kannst du das spezifische Modell wählen. Es wird
              für alle Chats und Karten-Generierungen verwendet, bis du es änderst.
            </p>
          </header>
          <ModelSelector
            provider={selectedProvider}
            selectedModel={settings.model}
            onSelect={handleModelChange}
          />
        </section>
      )}

      {settings.provider === 'ollama' && (
        <section className="settings-section-card" aria-labelledby="ai-ollama-heading">
          <header className="settings-section-head">
            <h2 className="settings-section-title" id="ai-ollama-heading">
              Ollama-Konfiguration
            </h2>
            <p className="settings-section-desc">{t('settings.ollamaDesc')}</p>
          </header>
          <OllamaConfig
            baseUrl={settings.ollamaBaseUrl}
            onBaseUrlChange={handleOllamaUrlChange}
            selectedModel={settings.model}
            onModelSelect={handleModelChange}
          />
        </section>
      )}

      <section
        className="settings-section-card settings-danger"
        aria-labelledby="ai-reset-heading"
      >
        <header className="settings-section-head">
          <h2 className="settings-section-title" id="ai-reset-heading">
            Erweitert
          </h2>
          <p className="settings-section-desc">
            Setze die KI-Konfiguration auf den Standard zurück. Dies ändert keine gespeicherten
            API-Keys.
          </p>
        </header>
        <div className="form-actions" style={{ marginTop: 0, padding: 0 }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              settings.resetSettings();
              showSaved();
            }}
          >
            <RefreshCw size={16} aria-hidden="true" />
            <span>{t('settings.resetButton', 'Standard wiederherstellen')}</span>
          </button>
        </div>
      </section>
    </div>
  );
}
